@if (initialLoading && !finished) {
    <basket-overview-skeleton [isMobile]="!!isMobile"></basket-overview-skeleton>
}
@if (supplierBaskets) {
    <!-- Kept mounted (only visually hidden) while the initial skeleton is shown, so the basket's
         items and their accounting validations (incl. settings) load behind the skeleton; the
         initial loading state only settles once those have finished (EPROC-11165). -->
    <div class="container-fluid" [class.d-none]="initialLoading && !finished">
        @if (!finished) {
            <h2 class="basket-overview-title">
                <em class="fas fa-shopping-cart"></em>
                {{ 'Warenkorb' | translate }}
            </h2>
        } @else {
            <h2 class="basket-overview-title mb-5">
                <em class="fas fa-shopping-cart"></em>
                {{ 'Bestellübersicht' | translate }}
            </h2>
        }
        @if (!finished) {
            @if (targetUser$ | async; as targetUser) {
                <div class="container-md">
                    <div class="alert alert-info d-flex align-items-center justify-content-between">
                        <span>
                            <em class="fas fa-user me-2"></em>
                            {{
                                'Sie bestellen im Namen von {{name}}'
                                    | translate: { name: targetUser.displayName }
                            }}
                        </span>
                        <button
                            type="button"
                            class="btn btn-link p-0"
                            (click)="clearOrderOnBehalfTarget()"
                        >
                            {{ 'Entfernen' | translate }}
                        </button>
                    </div>
                </div>
            }
            <div class="basket-stepper">
                <ol class="stepper-header">
                    @for (label of stepLabels; track $index) {
                        <li
                            class="stepper-header_step"
                            [class.is-active]="$index === activeIndex"
                            [class.is-done]="$index < activeIndex || orderState === orderStateEnum.ORDERSUCCESSFUL"
                            [class.is-clickable]="canGoToStep($index)"
                        >
                            <button
                                type="button"
                                class="stepper-header_circle"
                                [attr.aria-current]="$index === activeIndex ? 'step' : null"
                                [disabled]="!canGoToStep($index)"
                                (click)="goToStep($index)"
                            >
                                @if ($index < activeIndex || orderState === orderStateEnum.ORDERSUCCESSFUL) {
                                    <em class="fas fa-check"></em>
                                } @else if (stepIcons[$index]) {
                                    <em [class]="stepIcons[$index]"></em>
                                } @else {
                                    {{ $index + 1 }}
                                }
                            </button>
                            <span class="stepper-header_label">{{ label | translate }}</span>
                        </li>
                    }
                </ol>
                <div class="stepper-header_active-label">{{ stepLabels[activeIndex] | translate }}</div>

                <div class="stepper-viewport" [@stepSlide]="activeIndex">
                    @if (activeIndex === 0) {
                        <div class="stepper-panel">
                            @if (basket && supplierBaskets.length) {
                                @if (showCompanyCodeSelection) {
                                    <div class="container-lg">
                                        <div class="card shadow-sm mb-3">
                                            <div class="card-header d-flex align-items-center">
                                                <h3 class="mb-0 flex-grow-1">
                                                    <em class="iim iim-accounting pe-2"></em>
                                                    {{ 'Buchungskreis' | translate }}
                                                </h3>
                                                <span
                                                    class="text-muted cursor-pointer"
                                                    title="{{
                                                        'Der Buchungskreis steuert Kontierungs- und Freigabeeinstellungen. Bitte wählen Sie den passenden Buchungskreis, bevor Sie fortfahren.'
                                                            | translate
                                                    }}"
                                                >
                                                    <em class="fas fa-circle-info"></em>
                                                </span>
                                            </div>
                                            <div class="card-body">
                                                @if (companyCodeOptionsLoading) {
                                                    <div class="d-flex align-items-center text-description">
                                                        <em class="fas fa-spinner fa-spin me-2"></em>
                                                        {{ 'Buchungskreise' | translate }}
                                                    </div>
                                                } @else if (companyCodeLoadFailed) {
                                                    <div class="alert alert-warning mb-0">
                                                        {{ 'Fehler beim Laden der Buchungskreise' | translate }}
                                                    </div>
                                                } @else if (!companyCodes.length) {
                                                    <div class="alert alert-info mb-0">
                                                        {{ 'Sie haben noch keine Buchungskreise angelegt' | translate }}
                                                    </div>
                                                } @else if (showCompanyCodeSelection) {
                                                    <label class="form-label d-flex align-items-center">
                                                        {{ 'Buchungskreis' | translate }}
                                                    </label>
                                                    @if (companyCodes.length > 3) {
                                                        <input
                                                            class="form-control mb-2"
                                                            type="text"
                                                            [placeholder]="'Buchungskreis suchen' | translate"
                                                            [(ngModel)]="companyCodeFilter"
                                                        />
                                                    }
                                                    <select
                                                        class="form-select"
                                                        [ngModel]="selectedCompanyCodeId"
                                                        (ngModelChange)="handleCompanyCodeSelection($event)"
                                                        [disabled]="companyCodeUpdating"
                                                    >
                                                        <option [ngValue]="null" disabled>
                                                            {{ 'Bitte wählen Sie einen Buchungskreis aus' | translate }}
                                                        </option>
                                                        @for (
                                                            companyCode of filteredCompanyCodes;
                                                            track companyCode.id
                                                        ) {
                                                            <option [ngValue]="companyCode.id">
                                                                {{ companyCode.code }} - {{ companyCode.name }}
                                                            </option>
                                                        }
                                                    </select>
                                                    @if (companyCodeUpdating) {
                                                        <div class="d-flex align-items-center text-description mt-2">
                                                            <em class="fas fa-spinner fa-spin me-2"></em>
                                                            {{ 'Speichern' | translate }}
                                                        </div>
                                                    }
                                                } @else {
                                                    <div>
                                                        <div class="text-description mb-1">
                                                            {{ 'Buchungskreis' | translate }}
                                                        </div>
                                                        <div class="fw-bold">
                                                            {{ companyCodeLabel(selectedCompanyCodeId) }}
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                }
                                @if (requiresCompanyCodeSelection) {
                                    <div class="container-lg">
                                        <div class="alert alert-info">
                                            {{
                                                'Bitte wählen Sie zuerst einen Buchungskreis aus. Danach werden alle Felder des Warenkorbs freigeschaltet.'
                                                    | translate
                                            }}
                                        </div>
                                    </div>
                                } @else {
                                    @if (basketReloading) {
                                        <div
                                            class="container-md basket-reloading"
                                            role="status"
                                            aria-live="polite"
                                            data-cy="BASKET_RELOADING"
                                        >
                                            <em class="fas fa-spinner fa-spin basket-reloading_spinner"></em>
                                            <span>{{ 'Warenkorb wird aktualisiert …' | translate }}</span>
                                        </div>
                                    }
                                    <!-- Kept in the DOM (only visually hidden) while reloading so the
                                     per-item accounting validations keep running and the loading
                                     state can settle correctly (EPROC-11028). -->
                                    <div
                                        class="basket-supplier-wrapper container-md p-2"
                                        [class.d-none]="basketReloading"
                                    >
                                        <div class="basket-supplier-wrapper_button container-md">
                                            <accounting-change
                                                [editCostCenter]="true"
                                                [editProject]="true"
                                                [editGeneralLedgerAccount]="true"
                                                [editCustomAccounting]="true"
                                                [companyCodeId]="selectedCompanyCodeId ?? ''"
                                                [showMaterialGroup]="true"
                                                [showPurchasingGroupPreview]="purchasingGroupVisible"
                                                (accountingsChanged)="setAllAccountingOptionsAtOnce($event)"
                                            ></accounting-change>
                                            @if (purchaseRequisitionStatus) {
                                                <button class="btn btn-outline-dark ms-1" (click)="onDemand()">
                                                    {{ 'In Bedarfsmeldung wandeln' | translate }}
                                                </button>
                                            }
                                            @if (hasBasketItems) {
                                                <button class="btn btn-outline-danger ms-1" (click)="onClearBasket()">
                                                    {{ 'Warenkorb leeren' | translate }}
                                                </button>
                                            }
                                            @if (isOrderOnBehalfAllowed) {
                                                <button
                                                    class="btn btn-outline-dark ms-1"
                                                    (click)="onSelectOrderOnBehalfTarget()"
                                                >
                                                    <em class="fas fa-user pe-1"></em>
                                                    {{ 'Bestellen im Namen von' | translate }}
                                                </button>
                                            }
                                        </div>
                                        <suppliers-basket-feature
                                            [showStepperButtons]="true"
                                            [isEditableStep]="true"
                                            [budgetOverruns]="budgetOverruns"
                                            [purchasingGroupAssignmentIssues]="purchasingGroupAssignmentIssues"
                                            [resolvedPurchasingGroups]="resolvedPurchasingGroups"
                                            [purchasingGroupVisible]="purchasingGroupVisible"
                                            [purchasingGroupValidationLoading]="purchasingGroupValidationLoading"
                                            [companyCodeId]="selectedCompanyCodeId"
                                            (nextClicked)="openAddressSelection()"
                                            (validityChanged)="stepGroup?.controls.supplierOverview.setValue($event)"
                                        ></suppliers-basket-feature>
                                    </div>
                                }
                            }
                        </div>
                    } @else if (activeIndex === 1) {
                        <div class="stepper-panel">
                            <basket-addresses
                                [basket]="basket"
                                [supplierBaskets]="supplierBaskets"
                                [isEditableStep]="true"
                                [showStepperButtons]="true"
                                [deliveryAddress]="deliveryAddress"
                                [invoiceAddress]="invoiceAddress"
                                [isMoreThanOneInvoiceAddress]="isMoreThanOneInvoiceAddress"
                                (nextClicked)="openBasketCheck()"
                                (previousClicked)="openBasketSupplier()"
                                (validityChanged)="stepGroup?.controls.basketAddressSelection.setValue($event)"
                                (deliveryAddressDialogOpened)="openDeliveryAddressDialog()"
                                (invoiceAddressDialogOpened)="openInvoiceAddressDialog()"
                                (transmissionInfoUpdated)="updateTransmissionInfo($event)"
                                (contactPersonsUpdated)="updateContactPersons($event)"
                            ></basket-addresses>
                        </div>
                    } @else if (activeIndex === 2) {
                        <div class="stepper-panel">
                            <basket-check
                                class="mat-step-3"
                                [supplierBaskets]="supplierBaskets"
                                [currencyTotals]="basket?.currencyTotals ?? []"
                                [deliveryAddress]="deliveryAddress"
                                [showStepperButtons]="false"
                                [invoiceAddress]="invoiceAddress"
                                [isMobile]="isMobile"
                                [isEditableStep]="false"
                                [priceIsZero]="priceIsZero"
                                [purchaseRequisitionStatus]="purchaseRequisitionStatus"
                                [isOrderCreationAllowed]="isOrderCreationAllowed"
                                [isOrderCompletionAllowed]="isOrderCompletionAllowed()"
                                [budgetOverruns]="budgetOverruns"
                                [companyCodeId]="selectedCompanyCodeId"
                                [resolvedPurchasingGroups]="resolvedPurchasingGroups"
                                [purchasingGroupVisible]="purchasingGroupVisible"
                                (validityChanged)="stepGroup.controls.basketCheck.setValue($event)"
                                (ordered)="onCheckout()"
                                (demanded)="onDemand()"
                                (previousClicked)="openBasketSupplier()"
                            ></basket-check>
                        </div>
                    } @else if (activeIndex === 3) {
                        <div class="stepper-panel">
                            <basket-task-container #taskContainer></basket-task-container>
                        </div>
                    }
                </div>
            </div>
        }
    </div>
}

@if (orderState === orderStateEnum.ORDERFAILED) {
    <div>
        <section class="container-md mt-3">
            <div class="alert alert-warning">
                {{ orderMessage }}
            </div>
        </section>
    </div>
}

@if (!initialLoading && basket && supplierBaskets.length === 0 && orderState === orderStateEnum.NOTORDERED) {
    <div>
        <div class="container-md">
            <div class="row">
                <div class="col mx-2 alert alert-info mb-2">
                    {{ 'Es befinden sich keine Produkte im Warenkorb' | translate }}
                </div>
            </div>
        </div>
    </div>
}

@if (orderState === orderStateEnum.ORDERSUCCESSFUL && finished) {
    <div>
        <basket-order [order]="order"></basket-order>
    </div>
}
